import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { MdClose, MdDeleteOutline, MdDownload, MdFiberManualRecord, MdPlayArrow, MdStop, MdVideocam } from 'react-icons/md'
import ConfirmModal from '@/components/confirm-modal'
import {
    deleteVideoRecording,
    getVideoRecordingDownload,
    listVideoRecordings,
    startVideoRecording,
    stopVideoRecording,
    type VideoRecording,
    type VideoRecordingStatus,
    type VideoRoom,
} from '@/lib/api/video/video-api'

const STATUS_META: Record<VideoRecordingStatus, { label: string; className: string }> = {
    STARTING: { label: 'Iniciando', className: 'badge-warning' },
    RECORDING: { label: 'Gravando', className: 'badge-error' },
    PROCESSING: { label: 'Processando', className: 'badge-info' },
    READY: { label: 'Pronta', className: 'badge-success' },
    FAILED: { label: 'Falhou', className: 'badge-ghost' },
}

/** Enquanto houver gravação em andamento ou processando, a lista se atualiza sozinha. */
const POLL_INTERVAL_MS = 5000

function formatDuration(seconds: number): string {
    if (!seconds) return '-'
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const rest = seconds % 60
    return hours > 0
        ? `${hours}h ${String(minutes).padStart(2, '0')}min`
        : `${minutes}min ${String(rest).padStart(2, '0')}s`
}

function formatSize(bytes: number): string {
    if (!bytes) return '-'
    const mb = bytes / (1024 * 1024)
    return mb >= 1024 ? `${(mb / 1024).toFixed(2)} GB` : `${mb.toFixed(1)} MB`
}

function formatDateTime(value: string): string {
    return new Date(value).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })
}

/**
 * Gravações de uma sala: assistir, baixar, apagar e, com a reunião em andamento,
 * começar ou parar a gravação. O arquivo fica no armazenamento da organização.
 */
export default function VideoRecordingsModal({
    room,
    canEdit,
    canDelete,
    onClose,
}: {
    room: VideoRoom
    canEdit: boolean
    canDelete: boolean
    onClose: () => void
}) {
    const [recordings, setRecordings] = useState<VideoRecording[] | null>(null)
    const [busy, setBusy] = useState(false)
    const [confirmStart, setConfirmStart] = useState(false)
    const [toDelete, setToDelete] = useState<VideoRecording | null>(null)
    const onCloseRef = useRef(onClose)
    onCloseRef.current = onClose

    const load = useCallback(async () => {
        const response = await listVideoRecordings(room.id)
        if (response.success && response.result) setRecordings(response.result)
        else {
            toast.error(response.error || 'Não foi possível carregar as gravações')
            onCloseRef.current()
        }
    }, [room.id])

    useEffect(() => {
        void load()
    }, [load])

    const active = recordings?.find(item => item.status === 'STARTING' || item.status === 'RECORDING') ?? null
    const pending = recordings?.some(item => ['STARTING', 'RECORDING', 'PROCESSING'].includes(item.status)) ?? false

    useEffect(() => {
        if (!pending) return
        const timer = window.setInterval(() => void load(), POLL_INTERVAL_MS)
        return () => window.clearInterval(timer)
    }, [pending, load])

    const start = async () => {
        setBusy(true)
        const response = await startVideoRecording(room.id)
        setBusy(false)
        setConfirmStart(false)
        if (!response.success) {
            toast.error(response.error || 'Não foi possível começar a gravação')
            return
        }
        toast.success('Gravação iniciada. Todos na sala foram avisados.')
        void load()
    }

    const stop = async () => {
        setBusy(true)
        const response = await stopVideoRecording(room.id)
        setBusy(false)
        if (!response.success) {
            toast.error(response.error || 'Não foi possível parar a gravação')
            return
        }
        toast.success('Gravação parada. O arquivo fica pronto em instantes.')
        void load()
    }

    const open = async (recording: VideoRecording, mode: 'play' | 'download') => {
        const response = await getVideoRecordingDownload(recording.id, mode)
        if (!response.success || !response.result) {
            toast.error(response.error || 'Não foi possível abrir a gravação')
            return
        }
        window.open(response.result.url, '_blank', 'noopener')
    }

    const remove = async () => {
        if (!toDelete) return
        setBusy(true)
        const response = await deleteVideoRecording(toDelete.id)
        setBusy(false)
        if (!response.success) {
            toast.error(response.error || 'Não foi possível apagar a gravação')
            return
        }
        setToDelete(null)
        toast.success('Gravação apagada')
        void load()
    }

    return (
        <div className="modal modal-open">
            <div className="modal-box max-w-2xl">
                <div className="flex items-center justify-between mb-1">
                    <h3 className="text-lg font-semibold flex items-center gap-2">
                        <MdVideocam className="text-xl text-primary" />
                        Gravações
                    </h3>
                    <button type="button" className="btn btn-sm btn-ghost btn-circle" onClick={onClose}>
                        <MdClose className="text-xl" />
                    </button>
                </div>
                <p className="text-sm text-base-content/70 mb-5 truncate">{room.title || `Sala #${room.id}`}</p>

                {canEdit && room.status === 'ACTIVE' && (
                    <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-base-200/60 px-4 py-3 mb-4">
                        <span className="text-sm">
                            {active ? 'A reunião está sendo gravada.' : 'A reunião está em andamento.'}
                        </span>
                        {active ? (
                            <button type="button" className="btn btn-sm btn-error" disabled={busy} onClick={stop}>
                                <MdStop />
                                Parar gravação
                            </button>
                        ) : (
                            <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                disabled={busy}
                                onClick={() => setConfirmStart(true)}
                            >
                                <MdFiberManualRecord />
                                Gravar agora
                            </button>
                        )}
                    </div>
                )}

                {!recordings ? (
                    <div className="flex justify-center py-8">
                        <span className="loading loading-spinner loading-md text-primary" />
                    </div>
                ) : recordings.length === 0 ? (
                    <p className="text-sm text-base-content/60 py-6 text-center">
                        Nenhuma gravação desta sala. O moderador grava pelo botão de gravação na reunião, ou a sala
                        pode gravar automaticamente.
                    </p>
                ) : (
                    <ul className="space-y-2">
                        {recordings.map(item => {
                            const status = STATUS_META[item.status]
                            return (
                                <li
                                    key={item.id}
                                    className="rounded-lg border border-base-200 px-3 py-2.5 flex flex-wrap items-center justify-between gap-3"
                                >
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="text-sm font-medium">{formatDateTime(item.startedAt)}</span>
                                            <span className={`badge badge-sm ${status.className}`}>{status.label}</span>
                                        </div>
                                        <div className="text-xs text-base-content/60 mt-0.5">
                                            {formatDuration(item.durationSeconds)} · {formatSize(item.sizeBytes)}
                                            {item.status === 'FAILED' && item.error ? ` · ${item.error}` : ''}
                                        </div>
                                    </div>
                                    <div className="flex gap-1">
                                        {item.status === 'READY' && (
                                            <>
                                                <button
                                                    type="button"
                                                    className="btn btn-xs btn-ghost"
                                                    onClick={() => void open(item, 'play')}
                                                    title="Assistir"
                                                >
                                                    <MdPlayArrow className="text-base" />
                                                    Assistir
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-xs btn-ghost"
                                                    onClick={() => void open(item, 'download')}
                                                    title="Baixar"
                                                >
                                                    <MdDownload className="text-base" />
                                                </button>
                                            </>
                                        )}
                                        {canDelete && !['STARTING', 'RECORDING', 'PROCESSING'].includes(item.status) && (
                                            <button
                                                type="button"
                                                className="btn btn-xs btn-ghost text-error"
                                                onClick={() => setToDelete(item)}
                                                title="Apagar"
                                            >
                                                <MdDeleteOutline className="text-base" />
                                            </button>
                                        )}
                                    </div>
                                </li>
                            )
                        })}
                    </ul>
                )}

                <div className="modal-action">
                    <button type="button" className="btn btn-ghost" onClick={onClose}>
                        Fechar
                    </button>
                </div>
            </div>
            <div className="modal-backdrop" onClick={onClose} />

            <ConfirmModal
                isOpen={confirmStart}
                onClose={() => setConfirmStart(false)}
                onConfirm={() => void start()}
                loading={busy}
                title="Gravar a reunião"
                message="Todos na sala serão avisados de que a reunião está sendo gravada. O minuto gravado consome créditos de vídeo e o arquivo ocupa o armazenamento da organização."
                confirmButtonText="Gravar"
                confirmButtonClass="btn btn-primary !border-0 !rounded-lg"
            />
            <ConfirmModal
                isOpen={!!toDelete}
                onClose={() => setToDelete(null)}
                onConfirm={() => void remove()}
                loading={busy}
                title="Apagar gravação"
                message="O arquivo é apagado de vez e o espaço volta para o armazenamento da organização."
                confirmButtonText="Apagar"
            />
        </div>
    )
}
